import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import cs from './styles.module.scss';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Subscribe to the OS "reduce motion" setting so the JS-driven typewriter can be
// disabled to match the CSS `prefers-reduced-motion` media queries.
const usePrefersReducedMotion = (): boolean =>
  React.useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(REDUCED_MOTION_QUERY);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false // SSR / no window: assume motion is allowed.
  );

interface Props {
  // Cycled through in order, looping back to the first once the last finishes.
  phrases: string[];
  // Applied to the container alongside the base layout class — set font, weight,
  // and color here.
  className?: string;
  // Stable label announced to screen readers in place of the char-by-char
  // animation. Defaults to the first phrase.
  ariaLabel?: string;
}

// Types each phrase out, holds, deletes, then advances to the next — looping
// while mounted. Reduced-motion users get a single static phrase with no caret.
// `role="img"` gives screen readers one stable label instead of the char-by-char
// churn.
const TypewriterStatus: React.FC<Props> = ({phrases, className, ariaLabel}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [text, setText] = useState(() => (prefersReducedMotion ? phrases[0] : ''));

  // Hold the latest phrases in a ref, keyed by content, so the loop restarts
  // only when the phrases actually change — not when a caller passes a fresh
  // array literal on every render.
  const phrasesRef = React.useRef(phrases);
  phrasesRef.current = phrases;
  const phrasesKey = phrases.join('|');

  useEffect(() => {
    if (prefersReducedMotion) {
      setText(phrasesRef.current[0]);
      return;
    }
    const list = phrasesRef.current;
    let phraseIndex = 0;
    let charCount = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const full = list[phraseIndex];
      let delay: number;
      if (!deleting) {
        charCount++;
        if (charCount >= full.length) {
          deleting = true;
          delay = 1600;
        } else {
          delay = 55 + Math.random() * 45;
        }
      } else {
        charCount--;
        if (charCount <= 0) {
          deleting = false;
          phraseIndex = (phraseIndex + 1) % list.length;
          delay = 350;
        } else {
          delay = 30;
        }
      }
      setText(full.slice(0, charCount));
      timer = setTimeout(tick, delay);
    };
    tick();
    return () => clearTimeout(timer);
  }, [phrasesKey, prefersReducedMotion]);

  return (
    <div
      className={classNames(cs.typewriter, className)}
      role="img"
      aria-label={ariaLabel ?? phrases[0]}
    >
      <span aria-hidden="true">{text}</span>
      {!prefersReducedMotion && <span className={cs.caret} aria-hidden="true" />}
    </div>
  );
};

export default TypewriterStatus;
